import { ReactElement, ReactNode, isValidElement } from 'react';

/**
 * Determines if a ReactNode contains valid, renderable content
 * that should trigger panel visibility.
 */
export function isValidContent(content: ReactNode): boolean {
  // Null, undefined, empty string, or false should not trigger panel visibility
  if (content == null || content === '' || content === false) {
    return false;
  }

  // Numbers and true are valid content
  if (typeof content === 'number' || content === true) {
    return true;
  }

  // Strings with content are valid
  if (typeof content === 'string') {
    return content.trim().length > 0;
  }

  // React elements are valid if they exist
  if (isValidElement(content)) {
    return true;
  }

  // Arrays are valid if they contain at least one valid element
  if (Array.isArray(content)) {
    return content.some(isValidContent);
  }

  // Objects (like React fragments) are considered valid
  if (typeof content === 'object') {
    return true;
  }

  return false;
}

/**
 * Checks if content represents an "empty" state that should hide panels
 */
export function isEmptyContent(content: ReactNode): boolean {
  return !isValidContent(content);
}

/**
 * Gets a human-readable description of content for debugging
 */
export function getContentDescription(content: ReactNode): string {
  if (content == null) return 'null/undefined';
  if (content === '') return 'empty string';
  if (content === false) return 'false';
  if (content === true) return 'true';
  if (typeof content === 'string') return `string: "${content.slice(0, 50)}${content.length > 50 ? '...' : ''}"`;
  if (typeof content === 'number') return `number: ${content}`;
  if (Array.isArray(content)) return `array[${content.length}]`;
  if (isValidElement(content)) {
    const element = content as ReactElement;
    return `React element: ${element.type?.toString?.() || 'unknown'}`;
  }
  return typeof content;
}

/**
 * Validates that a panel area string is a valid LayoutArea enum value
 */
export function isValidLayoutArea(area: string): boolean {
  const validAreas = ['left-sidebar', 'right-sidebar', 'bottom-panel', 'top-panel', 'content'];
  return validAreas.includes(area);
}

/**
 * Type guard to check if a value is a valid React component
 */
export function isReactComponent(value: unknown): value is React.ComponentType {
  return typeof value === 'function' || (typeof value === 'object' && value != null && 'render' in value);
}
